import { PageOuterContainer } from '../../root-components/layouts/page-outer-container'
import { PageHeading } from '../presentation-components/display-elements-components/page-heading'
import ReleaseNotesService from '../../shared/services/release-notes/release-notes-service'
import { SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IReleaseNote } from '../../shared/services/release-notes/release-notes-model'
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { TableDisplay } from '../presentation-components/miscellaneous-components/table-display'
import { InformationBox } from '../presentation-components/miscellaneous-components/information-box'
import { SearchBar } from '../presentation-components/display-elements-components/search-bar'
import { IconName } from '../../shared/interfaces/icon-picker.model'
import { IHeadCell } from '../../shared/interfaces/table-display.model'
import { LoadingPage } from './loading-page'
import { useDebounce } from '../../hooks/use-debounce-hook'
import { segmentedToggleSx } from '../../shared/utils/segmented-toggle-sx'
import { RELEASE_NOTE_TYPES, releaseNoteTypeRank } from '../../shared/constants/release-note-types'

// "Other" stays reachable through All; the filter offers the three kinds new notes are written as.
const TYPE_FILTERS = [
  { value: '', label: 'All' },
  ...RELEASE_NOTE_TYPES.filter(type => type.value !== 'Other').map(type => ({ value: type.value, label: type.filterLabel })),
]

const headCells: IHeadCell[] = []

export const ReleaseNotePage = () => {
  const releaseNotesService = new ReleaseNotesService()
  const [searchParams, setSearchParams] = useSearchParams()
  // Filters live in the URL so a filtered view can be linked to and survives navigation.
  const typeFilter = searchParams.get('type') ?? ''
  const urlSearch = searchParams.get('search') ?? ''

  const [releaseNotes, setReleaseNotes] = useState<IReleaseNote[]>()
  const [searchQuery, setSearchQuery] = useState(urlSearch)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const [selected, setSelected] = useState<string[]>([])
  const [inputPage, setInputPage] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const listTopRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    releaseNotesService.getAllReleased()
      .then(rows => setReleaseNotes(rows))
      .catch(() => setReleaseNotes([]))
  }, [])

  // Same URL sync as the surveys list: keyed on the debounced value only, so navigation that
  // clears ?search= wins over stale input.
  useEffect(() => {
    const current = searchParams.get('search') ?? ''
    if (debouncedSearchQuery === current) return
    const next = new URLSearchParams(searchParams)
    if (debouncedSearchQuery) {
      next.set('search', debouncedSearchQuery)
    } else {
      next.delete('search')
    }
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery])

  useEffect(() => {
    setSearchQuery(urlSearch)
  }, [urlSearch])

  const typeChangeHandler = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === null) return
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set('type', value)
    } else {
      next.delete('type')
    }
    setSearchParams(next, { replace: true })
  }

  const searchTerms = debouncedSearchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const searchKey = searchTerms.join(' ')

  // Newest first; on the same day, new features before improvements before fixes.
  const filteredNotes = useMemo(() => (releaseNotes ?? [])
    .filter(note => !typeFilter || note.status === typeFilter)
    .filter(note => {
      if (searchTerms.length === 0) return true
      const haystack = `${note.title} ${note.description}`.toLowerCase()
      return searchTerms.every(term => haystack.includes(term))
    })
    .sort((a, b) =>
      (new Date(b.releasedDate).getTime() - new Date(a.releasedDate).getTime())
      || (releaseNoteTypeRank(a.status) - releaseNoteTypeRank(b.status))
      || a.title.localeCompare(b.title)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [releaseNotes, typeFilter, searchKey])

  useEffect(() => {
    setInputPage(0)
  }, [typeFilter, searchKey])

  // Paging happens at the foot of a long list, so bring the reader back to the top of it.
  const changePage = (value: SetStateAction<number>) => {
    setInputPage(value)
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const rows = filteredNotes.slice(inputPage * perPage, (inputPage + 1) * perPage)
  const isFiltered = !!typeFilter || searchTerms.length > 0

  return (
    <PageOuterContainer>
      <PageHeading title='Release Notes' />
      <LoadingPage pageReady={!!releaseNotes}>
        {(releaseNotes ?? []).length === 0 ?
          <InformationBox
            text='There are currently no release notes'
            icon={IconName.EditNote}
          />
          :
          <>
            <Box
              ref={listTopRef}
              sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', scrollMarginTop: '80px' }}
            >
              <Box sx={{ width: { xs: '100%', sm: 320 } }}>
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} placeholder='Search release notes' />
              </Box>
              <ToggleButtonGroup
                size='small'
                exclusive
                value={typeFilter}
                onChange={typeChangeHandler}
                aria-label='Filter release notes by type'
                sx={segmentedToggleSx}
              >
                {TYPE_FILTERS.map(filter => (
                  <ToggleButton key={filter.value} value={filter.value}>{filter.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            {filteredNotes.length === 0 && isFiltered ?
              <InformationBox
                text='No release notes match your search'
                icon={IconName.EditNote}
              />
              :
              <TableDisplay
                isMobileMode={true}
                isReleaseNote={true}
                headCells={headCells}
                rows={rows}
                selected={selected}
                setSelected={setSelected}
                inputPage={inputPage}
                setInputPage={changePage}
                totalRecords={filteredNotes.length}
                perPage={perPage}
                setPerPage={setPerPage}
              />}
          </>}
      </LoadingPage>
    </PageOuterContainer>
  )
}
